import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  Home,
  Clipboard,
  Users,
  X,
  Shield,
  UserCog,
  Stethoscope,
  Video,
  CreditCard,
  Settings,
  LifeBuoy,
  IdCard,
  User,
  LogOut,
  LucideIcon
} from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'

export interface SideNavigationProps {
  isOpen: boolean
  onClose: () => void
  userRole?: string
}

interface NavItemConfig {
  icon: LucideIcon
  label: string
  href: string
  id: string
}

const navItems: NavItemConfig[] = [
  {
    icon: Home,
    label: 'Dashboard',
    href: '/u/dashboard',
    id: 'dashboard'
  },
  {
    icon: Clipboard,
    label: 'Programme',
    href: '/u/program',
    id: 'program'
  },
  {
    icon: Users,
    label: 'Supporter',
    href: '/u/supporter',
    id: 'supporter'
  }
]

const supporterNavItems: NavItemConfig[] = [
  {
    icon: Home,
    label: 'Dashboard',
    href: '/s/dashboard',
    id: 'supporter-dashboard'
  },
  {
    icon: Users,
    label: 'My Clients',
    href: '/s/clients',
    id: 'supporter-clients'
  },
  {
    icon: Video,
    label: 'Content Library',
    href: '/s/content-library',
    id: 'supporter-content'
  },
  {
    icon: IdCard,
    label: 'My Code',
    href: '/s/settings',
    id: 'supporter-code'
  },
  {
    icon: User,
    label: 'Account',
    href: '/u/account',
    id: 'supporter-account'
  }
]

const adminNavItems: NavItemConfig[] = [
  {
    icon: Shield,
    label: 'Admin Dashboard',
    href: '/admin',
    id: 'admin'
  },
  {
    icon: UserCog,
    label: 'User Management',
    href: '/admin/users',
    id: 'admin-users'
  },
  {
    icon: Stethoscope,
    label: 'Support Persons',
    href: '/admin/supporters',
    id: 'admin-supporters'
  },
  {
    icon: Video,
    label: 'Content Library',
    href: '/admin/videos',
    id: 'admin-videos'
  },
  {
    icon: CreditCard,
    label: 'Plans & Payments',
    href: '/admin/payments',
    id: 'admin-payments'
  },
  {
    icon: Settings,
    label: 'System Settings',
    href: '/admin/settings',
    id: 'admin-settings'
  },
  {
    icon: LifeBuoy,
    label: 'Support & Logs',
    href: '/admin/support',
    id: 'admin-support'
  }
]

export function SideNavigation({ isOpen, onClose, userRole = 'user' }: SideNavigationProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { signOut } = useAuthStore()
  const pathname = location.pathname
  const isAdmin = userRole === 'admin'
  const isSupporter = userRole === 'supporter'

  const isActive = (href: string) => {
    if (href === '/u/dashboard') {
      return pathname === '/u/dashboard'
    }
    if (href === '/s/dashboard') {
      return pathname === '/s/dashboard'
    }
    if (href === '/admin') {
      return pathname === '/admin'
    }
    return pathname?.startsWith(href)
  }

  const handleSignOut = async () => {
    await signOut()
    navigate('/auth/sign-in')
  }

  const renderNavItem = (item: NavItemConfig) => {
    const Icon = item.icon
    const active = isActive(item.href)

    return (
      <Link
        key={item.id}
        to={item.href}
        onClick={onClose}
        className={`
          w-full flex items-center gap-3 px-4 py-3 rounded-lg
          text-left transition-all cursor-pointer
          ${active
            ? 'bg-[#D4AF5A] text-white shadow-md'
            : 'text-white/70 hover:bg-white/10 hover:translate-x-1'
          }
          active:scale-[0.98]
        `}
      >
        <Icon className="w-5 h-5 flex-shrink-0" />
        <span className="font-medium text-sm">{item.label}</span>
      </Link>
    )
  }

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 lg:hidden z-40"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-screen w-64
          bg-[#0F1C2E] text-white z-50
          flex flex-col shadow-lg
          transition-transform duration-300 ease-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Header */}
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold">Drive Without Fear</h1>
          </div>
          <button
            onClick={onClose}
            className="lg:hidden p-2 hover:bg-white/10 rounded-lg transition-colors"
            aria-label="Close navigation menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-2 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {/* Supporter Navigation Items */}
          {isSupporter ? (
            supporterNavItems.map(renderNavItem)
          ) : (
            /* Regular Navigation Items for users */
            navItems.map(renderNavItem)
          )}

          {/* Admin Section — visible on both desktop and mobile-web so the
              slide-out sidebar mirrors the desktop nav exactly. */}
          {isAdmin && (
            <>
              {/* Divider */}
              <div className="pt-4 pb-2">
                <div className="border-t border-white/10 mb-4"></div>
                <div className="px-4 py-2">
                  <span className="text-xs font-semibold text-white/50 uppercase tracking-wider">
                    Admin
                  </span>
                </div>
              </div>

              {/* Admin Navigation Items */}
              <div className="space-y-2">
                {adminNavItems.map(renderNavItem)}
              </div>
            </>
          )}
        </nav>

        {/* Log Out Button */}
        <div className="p-4 border-t border-white/10">
          <button
            onClick={handleSignOut}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-white/70 hover:bg-white/10 transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium text-sm">Log Out</span>
          </button>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/10 text-xs text-white/70">
          <p>&copy; {new Date().getFullYear()} Drive Without Fear</p>
        </div>
      </aside>
    </>
  )
}
